import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserPlus, Search, Download, FileText, Shield, Key,
  UserX, ChevronRight, Activity, CheckCircle, XCircle,
} from 'lucide-react';
import PageHeader from '@/components/ui/PageHeader';
import DataTable, { Column } from '@/components/ui/DataTable';
import PillBadge from '@/components/ui/PillBadge';
import Modal from '@/components/ui/Modal';
import SideDrawer from '@/components/ui/SideDrawer';
import EmptyState from '@/components/ui/EmptyState';
import { TableSkeleton } from '@/components/ui/LoadingSkeleton';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useTableExport } from '@/hooks/useTableExport';

// ─── Types ────────────────────────────────────────────────────────────────────

type UserRole = 'admin' | 'qa_manager' | 'lab_manager' | 'analyst' | 'viewer';
type UserStatus = 'active' | 'inactive' | 'pending';

interface AppUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  labAssignments: string[];
  status: UserStatus;
  lastLogin: string | null;
  createdAt: string;
  phone?: string;
  [key: string]: unknown;
}

interface ActivityEntry {
  id: string;
  action: string;
  timestamp: string;
  module: string;
}

interface UserDetail extends AppUser {
  activityLog: ActivityEntry[];
}

interface InviteForm {
  name: string;
  email: string;
  role: UserRole;
  labAssignments: string[];
  message: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const ROLE_COLORS: Record<UserRole, string> = {
  admin: 'bg-purple-100 text-purple-700',
  qa_manager: 'bg-red-100 text-red-700',
  lab_manager: 'bg-blue-100 text-blue-700',
  analyst: 'bg-teal-100 text-teal-700',
  viewer: 'bg-gray-100 text-gray-600',
};

const ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Admin',
  qa_manager: 'QA Manager',
  lab_manager: 'Lab Manager',
  analyst: 'Analyst',
  viewer: 'Viewer',
};

const ALL_LABS = ['Lab A', 'Lab B', 'Lab C', 'Lab D', 'Lab E'];

const MODULE_PERMISSIONS: Record<UserRole, Record<string, { read: boolean; write: boolean; delete: boolean }>> = {
  admin: {
    CAPA: { read: true, write: true, delete: true },
    Audits: { read: true, write: true, delete: true },
    Equipment: { read: true, write: true, delete: true },
    Certifications: { read: true, write: true, delete: true },
    SOPs: { read: true, write: true, delete: true },
    Reports: { read: true, write: true, delete: true },
  },
  qa_manager: {
    CAPA: { read: true, write: true, delete: false },
    Audits: { read: true, write: true, delete: false },
    Equipment: { read: true, write: false, delete: false },
    Certifications: { read: true, write: true, delete: false },
    SOPs: { read: true, write: true, delete: false },
    Reports: { read: true, write: true, delete: false },
  },
  lab_manager: {
    CAPA: { read: true, write: true, delete: false },
    Audits: { read: true, write: false, delete: false },
    Equipment: { read: true, write: true, delete: false },
    Certifications: { read: true, write: false, delete: false },
    SOPs: { read: true, write: false, delete: false },
    Reports: { read: true, write: false, delete: false },
  },
  analyst: {
    CAPA: { read: true, write: false, delete: false },
    Audits: { read: true, write: false, delete: false },
    Equipment: { read: true, write: false, delete: false },
    Certifications: { read: true, write: false, delete: false },
    SOPs: { read: true, write: false, delete: false },
    Reports: { read: true, write: false, delete: false },
  },
  viewer: {
    CAPA: { read: true, write: false, delete: false },
    Audits: { read: true, write: false, delete: false },
    Equipment: { read: true, write: false, delete: false },
    Certifications: { read: true, write: false, delete: false },
    SOPs: { read: true, write: false, delete: false },
    Reports: { read: false, write: false, delete: false },
  },
};

const MOCK_USERS: AppUser[] = [
  { id: '1', name: 'Priya Sharma', email: 'priya@lab.com', role: 'admin', labAssignments: ['Lab A', 'Lab B'], status: 'active', lastLogin: '2026-04-03 09:12', createdAt: '2025-01-10' },
  { id: '2', name: 'Rajan Mehta', email: 'rajan@lab.com', role: 'qa_manager', labAssignments: ['Lab B'], status: 'active', lastLogin: '2026-04-02 14:30', createdAt: '2025-02-14' },
  { id: '3', name: 'Ananya Iyer', email: 'ananya@lab.com', role: 'lab_manager', labAssignments: ['Lab C'], status: 'active', lastLogin: '2026-04-01 11:00', createdAt: '2025-03-01' },
  { id: '4', name: 'Kiran Das', email: 'kiran@lab.com', role: 'analyst', labAssignments: ['Lab A'], status: 'inactive', lastLogin: '2026-03-20 08:45', createdAt: '2025-04-22' },
  { id: '5', name: 'Sneha Pillai', email: 'sneha@lab.com', role: 'viewer', labAssignments: [], status: 'pending', lastLogin: null, createdAt: '2026-04-03' },
];

const MOCK_ACTIVITY: ActivityEntry[] = [
  { id: 'a1', action: 'Logged in', timestamp: '2026-04-03 09:12', module: 'Auth' },
  { id: 'a2', action: 'Updated CAPA CAP-2024-0041', timestamp: '2026-04-02 16:30', module: 'CAPA' },
  { id: 'a3', action: 'Generated Audit Report', timestamp: '2026-04-01 14:00', module: 'Reports' },
  { id: 'a4', action: 'Approved SOP v3.2', timestamp: '2026-03-31 11:22', module: 'SOPs' },
  { id: 'a5', action: 'Invited user sneha@lab.com', timestamp: '2026-03-30 10:05', module: 'User Management' },
];

// ─── Role Badge ───────────────────────────────────────────────────────────────

function RoleBadge({ role }: { role: UserRole }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full ${ROLE_COLORS[role]}`}>
      {ROLE_LABELS[role]}
    </span>
  );
}

// ─── Permission Checklist ─────────────────────────────────────────────────────

function PermissionsGrid({ role }: { role: UserRole }) {
  const perms = MODULE_PERMISSIONS[role] || {};
  return (
    <div className="space-y-2">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-text-muted">
            <th className="text-left py-1 font-medium">Module</th>
            <th className="text-center py-1 font-medium w-14">Read</th>
            <th className="text-center py-1 font-medium w-14">Write</th>
            <th className="text-center py-1 font-medium w-14">Delete</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(perms).map(([mod, p]) => (
            <tr key={mod} className="border-t border-border">
              <td className="py-1.5 text-text">{mod}</td>
              {(['read', 'write', 'delete'] as const).map((k) => (
                <td key={k} className="text-center py-1.5">
                  {p[k]
                    ? <CheckCircle size={13} className="inline text-green-600" />
                    : <XCircle size={13} className="inline text-gray-300" />}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function UserManagement() {
  const { user: authUser } = useAuth();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AppUser | null>(null);
  const [form, setForm] = useState<InviteForm>({
    name: '', email: '', role: 'analyst', labAssignments: [], message: '',
  });

  const isAdmin = authUser?.role === 'admin';

  const { data, isLoading } = useQuery<{ users: AppUser[] }>({
    queryKey: ['users'],
    queryFn: () => api.get('/users').then((r) => r.data),
    staleTime: 60_000,
  });

  const users: AppUser[] = data?.users ?? MOCK_USERS;

  const filtered = useMemo(() => {
    return users.filter((u) => {
      const matchSearch = !search || u.name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase());
      const matchRole = !roleFilter || u.role === roleFilter;
      const matchStatus = !statusFilter || u.status === statusFilter;
      return matchSearch && matchRole && matchStatus;
    });
  }, [users, search, roleFilter, statusFilter]);

  const inviteMutation = useMutation({
    mutationFn: (payload: InviteForm) => api.post('/users/invite', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowInvite(false);
      setForm({ name: '', email: '', role: 'analyst', labAssignments: [], message: '' });
    },
  });

  const { exportCSV, exportPDF } = useTableExport(
    filtered as unknown as Record<string, unknown>[],
    [
      { key: 'name', label: 'Name' }, { key: 'email', label: 'Email' },
      { key: 'role', label: 'Role' }, { key: 'status', label: 'Status' },
      { key: 'lastLogin', label: 'Last Login' }, { key: 'createdAt', label: 'Created' },
    ],
    'users',
  );

  // Stats
  const stats = useMemo(() => ({
    total: users.length,
    active: users.filter((u) => u.status === 'active').length,
    inactive: users.filter((u) => u.status === 'inactive').length,
    pending: users.filter((u) => u.status === 'pending').length,
    admins: users.filter((u) => u.role === 'admin').length,
  }), [users]);

  const COLUMNS: Column<AppUser>[] = [
    { key: 'name', label: 'Name', render: (r) => <span className="font-medium text-text">{r.name}</span> },
    { key: 'email', label: 'Email', render: (r) => <span className="text-text-muted text-xs">{r.email}</span> },
    { key: 'role', label: 'Role', render: (r) => <RoleBadge role={r.role} /> },
    { key: 'labAssignments', label: 'Labs', render: (r) => <span className="text-xs text-text-muted">{r.labAssignments.join(', ') || '—'}</span> },
    { key: 'status', label: 'Status', render: (r) => <PillBadge value={r.status} /> },
    { key: 'lastLogin', label: 'Last Login', render: (r) => <span className="text-xs text-text-muted">{r.lastLogin ?? 'Never'}</span> },
    { key: 'createdAt', label: 'Created', render: (r) => <span className="text-xs text-text-muted">{r.createdAt}</span> },
    {
      key: 'id', label: '',
      render: (r) => (
        <button onClick={(e) => { e.stopPropagation(); setSelectedUser(r); }} className="p-1 text-text-muted hover:text-text">
          <ChevronRight size={14} />
        </button>
      ),
    },
  ];

  if (!isAdmin) {
    return (
      <div className="p-6">
        <EmptyState
          icon="alert"
          variant="error"
          title="Access Restricted"
          description="You must have admin privileges to manage users."
        />
      </div>
    );
  }

  const toggleLabAssignment = (lab: string) => {
    setForm((f) => ({
      ...f,
      labAssignments: f.labAssignments.includes(lab)
        ? f.labAssignments.filter((l) => l !== lab)
        : [...f.labAssignments, lab],
    }));
  };

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="User Management"
        subtitle="Provisioning · Roles · Access control"
        actions={
          <>
            <button onClick={exportCSV} className="btn-ghost flex items-center gap-2 text-sm">
              <Download size={14} /> CSV
            </button>
            <button onClick={exportPDF} className="btn-ghost flex items-center gap-2 text-sm">
              <FileText size={14} /> PDF
            </button>
            <button onClick={() => setShowInvite(true)} className="btn-primary flex items-center gap-2 text-sm">
              <UserPlus size={14} /> Invite User
            </button>
          </>
        }
      />

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: 'Total Users', value: stats.total, color: '#2563EB' },
          { label: 'Active', value: stats.active, color: '#16A34A' },
          { label: 'Inactive', value: stats.inactive, color: '#DC2626' },
          { label: 'Pending', value: stats.pending, color: '#D97706' },
          { label: 'Admins', value: stats.admins, color: '#7C3AED' },
        ].map((s) => (
          <div key={s.label} className="card p-4">
            <p className="text-xs text-text-muted font-medium mb-1">{s.label}</p>
            <p className="font-serif font-bold text-2xl" style={{ color: s.color }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-lg bg-card text-text placeholder:text-text-muted"
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="text-sm border border-border rounded-lg px-3 py-2 bg-card text-text"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
        >
          <option value="">All Roles</option>
          {(Object.keys(ROLE_LABELS) as UserRole[]).map((r) => (
            <option key={r} value={r}>{ROLE_LABELS[r]}</option>
          ))}
        </select>
        <select
          className="text-sm border border-border rounded-lg px-3 py-2 bg-card text-text"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="pending">Pending</option>
        </select>
      </div>

      {/* Table */}
      {isLoading ? (
        <TableSkeleton rows={6} />
      ) : (
        <DataTable<AppUser>
          columns={COLUMNS}
          data={filtered}
          keyField="id"
          onRowClick={setSelectedUser}
          tableId="users"
          emptyMessage="No users match your filters."
        />
      )}

      {/* Invite Modal */}
      <Modal
        isOpen={showInvite}
        onClose={() => setShowInvite(false)}
        title="Invite New User"
        subtitle="An invitation email will be sent to the user"
        size="md"
        footer={
          <>
            <button onClick={() => setShowInvite(false)} className="btn-ghost text-sm">Cancel</button>
            <button
              onClick={() => inviteMutation.mutate(form)}
              disabled={inviteMutation.isPending || !form.name || !form.email}
              className="btn-primary text-sm disabled:opacity-50"
            >
              {inviteMutation.isPending ? 'Sending…' : 'Send Invitation'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-text-muted mb-1">Full Name *</label>
              <input
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bg text-text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Jane Smith"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-text-muted mb-1">Email *</label>
              <input
                type="email"
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bg text-text"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="jane@lab.com"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-text-muted mb-1">Role *</label>
            <select
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bg text-text"
              value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as UserRole }))}
            >
              {(Object.keys(ROLE_LABELS) as UserRole[]).map((r) => (
                <option key={r} value={r}>{ROLE_LABELS[r]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-text-muted mb-2">Lab Assignments</label>
            <div className="grid grid-cols-2 gap-2">
              {ALL_LABS.map((lab) => (
                <label key={lab} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.labAssignments.includes(lab)}
                    onChange={() => toggleLabAssignment(lab)}
                    className="accent-teal-600"
                  />
                  <span className="text-sm text-text">{lab}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-text-muted mb-1">Personal Message (optional)</label>
            <textarea
              rows={3}
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bg text-text resize-none"
              placeholder="Welcome to the QMS platform…"
              value={form.message}
              onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
            />
          </div>
        </div>
      </Modal>

      {/* User Side Drawer */}
      {selectedUser && (
        <SideDrawer
          isOpen={!!selectedUser}
          onClose={() => setSelectedUser(null)}
          title={selectedUser.name}
          subtitle={selectedUser.email}
          width={500}
          footer={
            <div className="flex gap-2 w-full">
              <button className="btn-ghost text-sm flex items-center gap-2 flex-1 justify-center">
                <Key size={13} /> Reset Password
              </button>
              <button className="btn-ghost text-sm flex items-center gap-2 flex-1 justify-center text-red-600 border-red-200 hover:bg-red-50">
                <UserX size={13} /> Deactivate
              </button>
            </div>
          }
        >
          <div className="space-y-6">
            {/* Profile */}
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-teal-100 flex items-center justify-center font-serif font-bold text-teal-700 text-lg">
                {selectedUser.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
              </div>
              <div>
                <p className="font-semibold text-text">{selectedUser.name}</p>
                <RoleBadge role={selectedUser.role} />
                <div className="mt-1"><PillBadge value={selectedUser.status} showDot /></div>
              </div>
            </div>

            {/* Labs */}
            <div>
              <p className="text-xs font-semibold text-text-muted uppercase mb-2">Assigned Labs</p>
              <div className="flex flex-wrap gap-2">
                {selectedUser.labAssignments.length > 0
                  ? selectedUser.labAssignments.map((l) => (
                      <span key={l} className="px-2 py-1 text-xs rounded-lg bg-bg border border-border text-text">{l}</span>
                    ))
                  : <span className="text-sm text-text-muted">No labs assigned</span>}
              </div>
            </div>

            {/* Permissions */}
            <div>
              <p className="text-xs font-semibold text-text-muted uppercase mb-2 flex items-center gap-1">
                <Shield size={12} /> Role Permissions
              </p>
              <PermissionsGrid role={selectedUser.role} />
            </div>

            {/* Activity Log */}
            <div>
              <p className="text-xs font-semibold text-text-muted uppercase mb-2 flex items-center gap-1">
                <Activity size={12} /> Recent Activity
              </p>
              <div className="space-y-2">
                {MOCK_ACTIVITY.map((a) => (
                  <div key={a.id} className="flex items-start gap-3 p-2 rounded-lg bg-bg">
                    <div className="w-1.5 h-1.5 rounded-full bg-teal-500 mt-1.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-text">{a.action}</p>
                      <p className="text-xs text-text-muted">{a.module} · {a.timestamp}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </SideDrawer>
      )}
    </div>
  );
}
